/**
 * GroupDashboard.jsx - Vista Docente Multi-Grupo en Tiempo Real
 * 
 * Panel centralizado para docentes que facilita la supervisión y gestión de múltiples grupos:
 * - Vista consolidada de todas las secciones del docente
 * - Estado en tiempo real de entregables por grupo y semana
 * - Matriz de feedback cruzado con métricas de participación
 * - Estadísticas de progreso y colaboración por grupo
 * - Alertas automáticas de grupos rezagados o inactivos
 * - Exportación de reportes y analíticas para evaluación
 * 
 * Funcionalidades clave:
 * - Dashboard multi-sección con filtros por periodo/materia
 * - Cards de grupos con indicadores visuales de estado
 * - Timeline de actividad reciente por grupo
 * - Métricas de engagement y participación
 * - Herramientas de comunicación directa con grupos
 * 
 * Solo accesible para usuarios con rol teacher/admin
 * 
 * @component
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { 
  BarChart3,
  Users, 
  Calendar,
  Clock,
  TrendingUp,
  TrendingDown,
  Eye,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Pause,
  Play,
  Filter,
  Download,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  Settings,
  Bell,
  Target,
  Award,
  BookOpen,
  Edit3,
  Send,
  FileText,
  Activity,
  Zap,
  Layers,
  PieChart,
  Grid,
  List,
  ExternalLink,
  Plus
} from 'lucide-react';
import apiClient from '../services/apiClient';

const GroupDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Estados principales del dashboard
  const [sections, setSections] = useState([]);
  const [groups, setGroups] = useState([]);
  const [weeklyProgress, setWeeklyProgress] = useState({});
  const [feedbackMatrix, setFeedbackMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados de filtros y vista
  const [selectedSectionId, setSelectedSectionId] = useState('all');
  const [selectedWeek, setSelectedWeek] = useState('current');
  const [viewMode, setViewMode] = useState('cards'); // 'cards', 'table', 'timeline'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'completed', 'delayed'
  const [sortBy, setSortBy] = useState('progress'); // 'progress', 'activity', 'name', 'created'
  const [sortDirection, setSortDirection] = useState('desc');

  // Estados de métricas y estadísticas
  const [dashboardStats, setDashboardStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);

  // Estados de configuración y herramientas
  const [showFilters, setShowFilters] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 segundos
  const [selectedGroups, setSelectedGroups] = useState(new Set());

  // Referencias para auto-refresh
  const refreshIntervalRef = useRef(null);
  const lastRefreshRef = useRef(null);

  /**
   * Cargar datos del dashboard
   */
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Cargar secciones del docente (sistema CourseSection)
      const sectionsRes = await apiClient.get('/sections');
      const sectionsData = sectionsRes.data.sections || [];
      setSections(sectionsData);

      if (sectionsData.length === 0) {
        setLoading(false);
        return;
      }

      // Determinar sección activa
      const activeSectionId = selectedSectionId === 'all' ? 
        sectionsData.map(s => s.sectionId) : [selectedSectionId];

      // Cargar grupos de las secciones seleccionadas (sistema CourseGroup)
      // Usar el endpoint correcto: /teacher-dashboard/groups
      const groupsRes = await apiClient.get('/teacher-dashboard/groups', {
        params: { 
          sectionId: selectedSectionId === 'all' ? undefined : selectedSectionId 
        }
      }).catch(() => ({ data: { groups: [] } }));
      
      const allGroups = (groupsRes.data.groups || []).map(g => ({
        ...g,
        // groupName ya viene del backend, no sobreescribir
        members: (g.members || []).map(m => ({
          userId: m._id || m,
          userName: m.nombre || m.name || 'Miembro',
          email: m.email || ''
        }))
      }));
      setGroups(allGroups);

      // Semana activa: usar currentWeek real de la sección
      const activeSection = sectionsData.find(s => s.sectionId === (selectedSectionId !== 'all' ? selectedSectionId : sectionsData[0]?.sectionId));
      const currentWeek = selectedWeek === 'current' ? (activeSection?.currentWeek || 1) : parseInt(selectedWeek);
      try {
        const progressPromises = [];
        for (let week = 1; week <= 16; week++) {
          progressPromises.push(
            apiClient.get(`/groups/weekly-progress/week/${week}`, {
              params: { sectionIds: activeSectionId.join(',') }
            }).catch(() => ({ data: {} }))
          );
        }
        const progressResults = await Promise.all(progressPromises);
        const progressData = {};
        progressResults.forEach((res, index) => {
          progressData[index + 1] = res.data;
        });
        setWeeklyProgress(progressData);
      } catch (_) { /* endpoint opcional */ }

      // Cargar matriz de feedback (silencioso)
      try {
        const feedbackPromises = activeSectionId.map(sId =>
          apiClient.get(`/cross-feedback/${sId}/${currentWeek}/matrix`).catch(() => ({ data: {} }))
        );
        const feedbackResults = await Promise.all(feedbackPromises);
        const feedbackData = {};
        feedbackResults.forEach((res, index) => {
          feedbackData[activeSectionId[index]] = res.data;
        });
        setFeedbackMatrix(feedbackData);
      } catch (_) { /* endpoint opcional */ }

      // Cargar estadísticas del dashboard (silencioso)
      try {
        const statsRes = await apiClient.get('/teacher-dashboard/stats', {
          params: { sectionIds: activeSectionId.join(','), week: currentWeek }
        });
        setDashboardStats(statsRes.data);
      } catch (_) { /* endpoint opcional */ }

      // Cargar actividad reciente (silencioso)
      try {
        const activityRes = await apiClient.get('/teacher-dashboard/recent-activity', {
          params: { sectionIds: activeSectionId.join(','), week: currentWeek, limit: 20 }
        });
        setRecentActivity(activityRes.data);
      } catch (_) { /* endpoint opcional */ }

      // Cargar alertas automáticas (silencioso)
      try {
        const alertsRes = await apiClient.get('/teacher-dashboard/alerts', {
          params: { sectionIds: activeSectionId.join(',') }
        });
        setAlerts(alertsRes.data);
      } catch (_) { /* endpoint opcional */ }

      lastRefreshRef.current = new Date();

    } catch (error) {
      console.error('Error cargando dashboard:', error);
      setError('Error cargando el dashboard docente');
    } finally {
      setLoading(false);
    }
  }, [selectedSectionId, selectedWeek]);

  /**
   * Calcular semana actual basada en el dropdown seleccionado
   */
  const currentWeek = useMemo(() => {
    if (selectedWeek === 'current') {
      const activeSection = sections.find(s => 
        s.sectionId === (selectedSectionId !== 'all' ? selectedSectionId : sections[0]?.sectionId)
      );
      return activeSection?.currentWeek || 1;
    }
    return parseInt(selectedWeek);
  }, [selectedWeek, selectedSectionId, sections]);

  /**
   * Setup auto-refresh
   */
  useEffect(() => {
    loadDashboardData();

    if (autoRefresh && refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(loadDashboardData, refreshInterval);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [loadDashboardData, autoRefresh, refreshInterval]);

  /**
   * Filtrar y ordenar grupos
   */
  const getFilteredGroups = useCallback(() => {
    let filteredGroups = [...groups];

    // Filtro por término de búsqueda
    if (searchTerm) {
      filteredGroups = filteredGroups.filter(group =>
        group.groupName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.members.some(m => m.userName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filtro por estado
    if (statusFilter !== 'all') {
      filteredGroups = filteredGroups.filter(group => {
        const weekProgress = weeklyProgress[currentWeek]?.[group.groupId];
        
        switch (statusFilter) {
          case 'active':
            return weekProgress?.status === 'in_progress' || weekProgress?.status === 'active';
          case 'completed':
            return weekProgress?.status === 'completed';
          case 'delayed':
            return weekProgress?.delayedWeeks > 0;
          default:
            return true;
        }
      });
    }

    // Ordenamiento
    filteredGroups.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'progress':
          aValue = calculateGroupProgress(a);
          bValue = calculateGroupProgress(b);
          break;
        case 'activity':
          aValue = getGroupActivity(a);
          bValue = getGroupActivity(b);
          break;
        case 'name':
          aValue = a.groupName;
          bValue = b.groupName;
          break;
        case 'created':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return sortDirection === 'asc' ? 
          aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return filteredGroups;
  }, [groups, searchTerm, statusFilter, sortBy, sortDirection, weeklyProgress]);

  /**
   * Calcular progreso del entregable de la semana actual
   */
  const calculateGroupProgress = (group) => {
    // Basado en el estado del entregable de la semana seleccionada
    if (!group.weeklyDeliverable) {
      return 0; // No hay entregable = 0%
    }
    
    const status = group.weeklyDeliverable.status;
    
    // Mapear estado a porcentaje de progreso
    const statusToProgress = {
      'NotStarted': 0,
      'InProgress': 40,
      'ReadyForReview': 70,
      'Finalized': 90,
      'PendingCrossFeedback': 95,
      'Completed': 100
    };
    
    return statusToProgress[status] || 0;
  };

  /**
   * Obtener nivel de actividad de un grupo
   */
  const getGroupActivity = (group) => {
    // Basado en actividad reciente (últimas 48 horas) de la semana seleccionada en el dropdown
    const recent = recentActivity.filter(activity => {
      const isRecent = activity.groupId === group.groupId &&
        new Date(activity.timestamp) > new Date(Date.now() - 48 * 60 * 60 * 1000);
      
      // Si tiene metadata.week, filtrar por semana seleccionada
      const isCurrentWeek = !activity.metadata?.week || activity.metadata.week === currentWeek;
      return isRecent && isCurrentWeek;
    });
    
    return recent.length;
  };

  /**
   * Obtener nombre de la sección de un grupo
   */
  const getSectionName = (sectionId) => {
    const section = sections.find(s => s.sectionId === sectionId);
    return section?.name || section?.sectionId || 'Sección desconocida';
  };

  /**
   * Exportar datos del dashboard
   */
  const exportDashboardData = async () => {
    try {
      const exportRes = await apiClient.get('/teacher-dashboard/export', {
        params: {
          sectionIds: selectedSectionId === 'all' ? 
            sections.map(s => s.sectionId).join(',') : selectedSectionId,
          format: 'xlsx'
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([exportRes.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dashboard-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Error exportando datos del dashboard');
    }
  };

  /**
   * Seleccionar/deseleccionar grupos
   */
  const toggleGroupSelection = (groupId) => {
    const newSelection = new Set(selectedGroups);
    if (newSelection.has(groupId)) {
      newSelection.delete(groupId);
    } else {
      newSelection.add(groupId);
    }
    setSelectedGroups(newSelection);
  };

  const selectAllGroups = () => {
    const filteredGroups = getFilteredGroups();
    setSelectedGroups(new Set(filteredGroups.map(g => g.groupId)));
  };

  const clearGroupSelection = () => {
    setSelectedGroups(new Set());
  };

  // Verificar permisos de docente
  if (user?.role !== 'teacher' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acceso Restringido</h2>
          <p className="text-gray-600 mb-4">
            Solo los docentes pueden acceder al dashboard grupal.
          </p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
          >
            Ir al Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando dashboard docente...</p>
        </div>
      </div>
    );
  }

  // No sections state
  if (sections.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">No tienes secciones asignadas</h2>
          <p className="text-gray-600 mb-6">
            Para usar el dashboard docente, primero debes crear secciones y grupos.
          </p>
          <div className="space-y-3">
            <Link 
              to="/teaching/sections"
              className="block w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 text-center"
            >
              Gestionar Secciones
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const filteredGroups = getFilteredGroups();

  // Datos dinámicos para el hero del header
  const activeSection = sections.find(s =>
    s.sectionId === (selectedSectionId !== 'all' ? selectedSectionId : sections[0]?.sectionId)
  );
  // weekTitle viene directo del CourseDefinition vía /sections
  const displayWeekTitle = (() => {
    if (selectedWeek === 'current') {
      return activeSection?.weekTitle || `Semana ${currentWeek}`;
    }
    // Para semanas históricas el backend no devuelve weekTitle directamente,
    // pero si hay una sola sección podemos buscar en sus datos
    return activeSection?.weekTitle && activeSection?.currentWeek === currentWeek
      ? activeSection.weekTitle
      : `Semana ${currentWeek}`;
  })();
  const displayCourseTitle = activeSection?.courseTitle || null;
  const displaySectionName = selectedSectionId === 'all'
    ? 'Todas las secciones'
    : (activeSection?.name || '');

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── NIVEL 1: HERO – Contexto de lo que se está viendo ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">

            {/* Bloque izquierdo: breadcrumb + título jerárquico */}
            <div>
              {/* Breadcrumb */}
              <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-1" aria-label="Breadcrumb">
                <span>Dashboard Docente</span>
                {displayCourseTitle && (
                  <>
                    <span>/</span>
                    <span className="text-gray-600 font-medium">{displayCourseTitle}</span>
                  </>
                )}
              </nav>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                {displayWeekTitle}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {displaySectionName}
                <span className="mx-2 text-gray-300">·</span>
                Semana <span className="font-semibold text-gray-700">{currentWeek}</span>
                <span className="mx-2 text-gray-300">·</span>
                Actualizado: {lastRefreshRef.current?.toLocaleTimeString()}
              </p>
            </div>

            {/* Bloque derecho: acciones principales (solo 2) */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {sections.length > 0 && (
                <button
                  onClick={() => {
                    const targetId = selectedSectionId !== 'all' ? selectedSectionId : sections[0]?.sectionId;
                    if (targetId) navigate(`/teacher/${targetId}/settings`);
                  }}
                  className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium"
                  title="Configurar semana activa del curso"
                >
                  <Settings className="w-4 h-4 mr-1.5 text-gray-500" />
                  Configurar
                </button>
              )}
              <button
                onClick={() => {
                  const targetId = selectedSectionId !== 'all' ? selectedSectionId : sections[0]?.sectionId;
                  if (targetId) navigate(`/teaching/sections/${targetId}/groups`);
                }}
                className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                <Users className="w-4 h-4 mr-1.5" />
                Grupos
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── NIVEL 2: BARRA DE FILTROS PRINCIPALES ── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        {/* Scroll horizontal en móvil para no romper el layout */}
        <div className="overflow-x-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
            <div className="flex items-center gap-2 min-w-max sm:min-w-0 sm:flex-wrap">

              {/* Selector de sección */}
              <select
                value={selectedSectionId}
                onChange={(e) => setSelectedSectionId(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[140px]"
              >
                <option value="all">Todas las Secciones</option>
                {sections.map(section => (
                  <option key={section.sectionId} value={section.sectionId}>
                    {section.name}{section.semester ? ` · ${section.semester}` : ''}
                  </option>
                ))}
              </select>

              {/* Selector de semana */}
              <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[130px]"
              >
                <option value="current">Semana Actual</option>
                {Array.from({length: 16}, (_, i) => (
                  <option key={i+1} value={i+1}>Semana {i+1}</option>
                ))}
              </select>

              {/* Separador – solo en desktop */}
              <div className="hidden sm:block h-5 w-px bg-gray-200 mx-1" />

              {/* Filtros avanzados */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center px-3 py-2 text-sm rounded-lg border ${
                  showFilters
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Filtros</span>
              </button>

              {/* Auto-refresh */}
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`flex items-center px-3 py-2 text-sm rounded-lg border ${
                  autoRefresh
                    ? 'bg-green-50 border-green-300 text-green-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                title={autoRefresh ? 'Desactivar auto-refresco' : 'Activar auto-refresco'}
              >
                {autoRefresh ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </button>

              {/* Actualizar manual */}
              <button
                onClick={loadDashboardData}
                className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                title="Actualizar datos"
              >
                <RefreshCw className="w-4 h-4" />
              </button>

              {/* Exportar */}
              <button
                onClick={exportDashboardData}
                className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Download className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Exportar</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Panel de filtros expandible ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Búsqueda */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Buscar</label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Nombre de grupo o estudiante..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Filtro por estado */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">Todos</option>
                    <option value="active">Activos</option>
                    <option value="completed">Completados</option>
                    <option value="delayed">Retrasados</option>
                  </select>
                </div>

                {/* Ordenar por */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Ordenar por</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="progress">Progreso</option>
                    <option value="activity">Actividad</option>
                    <option value="name">Nombre</option>
                    <option value="created">Fecha creación</option>
                  </select>
                </div>

                {/* Dirección de ordenamiento */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Dirección</label>
                  <select
                    value={sortDirection}
                    onChange={(e) => setSortDirection(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="desc">Descendente</option>
                    <option value="asc">Ascendente</option>
                  </select>
                </div>
              </div>

              {/* Vista mode switcher */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <div className="flex space-x-2">
                  {[
                    { id: 'cards', label: 'Tarjetas', icon: Grid },
                    { id: 'table', label: 'Tabla', icon: List },
                    { id: 'timeline', label: 'Timeline', icon: Activity }
                  ].map(mode => {
                    const Icon = mode.icon;
                    return (
                      <button
                        key={mode.id}
                        onClick={() => setViewMode(mode.id)}
                        className={`flex items-center px-3 py-2 text-sm rounded-lg ${
                          viewMode === mode.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {mode.label}
                      </button>
                    );
                  })}
                </div>

                {/* Selección múltiple */}
                <div className="flex items-center space-x-2 text-sm">
                  <span className="text-gray-600">
                    {selectedGroups.size} de {filteredGroups.length} seleccionados
                  </span>
                  <button
                    onClick={selectAllGroups}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    Seleccionar todos
                  </button>
                  <button
                    onClick={clearGroupSelection}
                    className="text-gray-600 hover:text-gray-700"
                  >
                    Limpiar
                  </button>
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Alertas */}
      {alerts.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <Bell className="w-5 h-5 text-amber-600 mr-2" />
              <h3 className="font-medium text-amber-900">Alertas Automáticas</h3>
            </div>
            <div className="space-y-1">
              {alerts.slice(0, 3).map((alert, index) => (
                <p key={index} className="text-sm text-amber-800">
                  • {alert.message}
                </p>
              ))}
              {alerts.length > 3 && (
                <p className="text-xs text-amber-600 mt-2">
                  +{alerts.length - 3} alertas adicionales
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Estadísticas generales */}
      {dashboardStats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center">
                <Users className="w-8 h-8 text-blue-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {dashboardStats.totalGroups}
                  </div>
                  <div className="text-sm text-gray-600">Grupos Activos</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center">
                <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {dashboardStats.completionRate}%
                  </div>
                  <div className="text-sm text-gray-600">
                    Completitud Semana {dashboardStats.weekAnalyzed || 1}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center">
                <MessageSquare className="w-8 h-8 text-purple-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {dashboardStats.feedbackCount}
                  </div>
                  <div className="text-sm text-gray-600">Feedback Cruzado</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center">
                <Activity className="w-8 h-8 text-orange-600 mr-3" />
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {dashboardStats.averageActivity}
                  </div>
                  <div className="text-sm text-gray-600">
                    Coach IA Sem {dashboardStats.weekAnalyzed || 1} (3d)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        </div>
      )}

      {/* Contenido principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {filteredGroups.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron grupos</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter !== 'all' 
                ? 'Intenta ajustar los filtros de búsqueda'
                : 'No hay grupos en las secciones seleccionadas'
              }
            </p>
          </div>
        ) : (
          /* Vista de grupos - Cards mode por defecto */
          viewMode === 'cards' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGroups.map(group => {
                const isSelected = selectedGroups.has(group.groupId);

                // Ladrillos de avance — datos del deliverable de la semana seleccionada
                const deliverable = weeklyProgress[currentWeek]?.deliverables?.find(
                  d => d.groupId === group.groupId
                );
                const bricks = [
                  {
                    id: 'b2',
                    label: 'Producción',
                    done: deliverable
                      ? Object.keys(deliverable.b2_production?.formData || {}).length > 0
                      : false,
                  },
                  {
                    id: 'b4',
                    label: 'Mejora',
                    done: deliverable
                      ? deliverable.aiAnalysis?.hasAnalysis === true
                      : false,
                  },
                  {
                    id: 'b5',
                    label: 'Declaración',
                    done: deliverable
                      ? deliverable.status === 'Finalized' || deliverable.status === 'Completed'
                      : false,
                  },
                ];
                const bricksDone = bricks.filter(b => b.done).length;
                
                return (
                  <div
                    key={group.groupId}
                    className={`bg-white rounded-lg border-2 p-4 transition-all hover:shadow-md cursor-pointer ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => navigate(`/teaching/sections/${group.sectionId}/groups/${group.groupId}/weekly/${currentWeek}`)}
                  >
                    {/* Header: Nombre del grupo y sección + checkbox de selección */}
                    <div className="mb-4 flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">{group.groupName}</h3>
                        <p className="text-sm text-gray-500">{getSectionName(group.sectionId)}</p>
                      </div>
                      {/* Checkbox de selección múltiple – zona táctil 44px para móvil */}
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleGroupSelection(group.groupId); }}
                        className={`flex-shrink-0 w-7 h-7 rounded-md border-2 flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'bg-white border-gray-300 text-transparent hover:border-blue-400'
                        }`}
                        title="Seleccionar para exportar"
                        aria-label="Seleccionar grupo"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                    </div>

                    {/* Métricas del grupo */}
                    <div className="grid grid-cols-2 gap-4 pb-3 border-b border-gray-100">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900">
                          {group.members.length}
                        </div>
                        <div className="text-xs text-gray-600">Miembros</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900">
                          {(() => {
                            const total = group.totalMembers || group.members?.length || 0;
                            const active = group.activeMembersNow || 0;
                            return total > 0 ? Math.round((active / total) * 100) : 0;
                          })()}%
                        </div>
                        <div className="text-xs text-gray-600">Conectados</div>
                      </div>
                    </div>

                    {/* Ladrillos de avance semanal */}
                    <div className="py-3 border-b border-gray-100">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-gray-500">Avance semana {currentWeek}</span>
                        <span className={`text-xs font-semibold ${bricksDone === bricks.length ? 'text-green-600' : 'text-gray-500'}`}>
                          {bricksDone}/{bricks.length}
                        </span>
                      </div>
                      <div className="flex gap-1.5">
                        {bricks.map(brick => (
                          <div
                            key={brick.id}
                            title={brick.label}
                            className={`flex-1 h-2 rounded-full transition-colors ${
                              brick.done ? 'bg-green-500' : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <div className="flex gap-1.5 mt-1">
                        {bricks.map(brick => (
                          <div key={brick.id} className="flex-1 text-center">
                            <span className={`text-[10px] ${brick.done ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                              {brick.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Miembros del grupo */}
                    <div className="pt-3">
                      <div className="text-xs text-gray-500 mb-1">Miembros:</div>
                      <div className="flex flex-wrap gap-1">
                        {group.members.slice(0, 3).map(member => (
                          <span 
                            key={member.userId}
                            className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                          >
                            {member.userName}
                          </span>
                        ))}
                        {group.members.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{group.members.length - 3} más
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* Acciones para grupos seleccionados */}
        {selectedGroups.size > 0 && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-lg px-6 py-4">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {selectedGroups.size} grupos seleccionados
              </span>
              <button className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                <MessageSquare className="w-4 h-4 mr-2" />
                Enviar Mensaje
              </button>
              <button className="flex items-center px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
                <Download className="w-4 h-4 mr-2" />
                Exportar Selección
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupDashboard;